import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useFavoritesStore } from './store/favoritesStore';
import { ScrollToTop } from './components/ScrollToTop';
import { MetaPixelPageView } from './components/MetaPixelPageView';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { AdminGuard } from './components/ui/AdminGuard';
import { Toaster } from './components/ui/Toaster';
import { EmptyState } from './components/ui/EmptyState';
import { Button } from './components/ui/Button';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { CatalogPage } from './pages/CatalogPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProfilePage } from './pages/ProfilePage';
import { FavoritesPage } from './pages/FavoritesPage';
import { SettingsPage } from './pages/SettingsPage';
import { PROMOTION_CAMPAIGN_ENABLED, getCampaignCatalogHref } from './config/promotionCampaign';

function AppContent() {
  const logout = useAuthStore((s) => s.logout);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loadFavoriteIds = useFavoritesStore((s) => s.loadFavoriteIds);
  const resetFavorites = useFavoritesStore((s) => s.reset);

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [logout]);

  useEffect(() => {
    if (isAuthenticated) {
      loadFavoriteIds();
    } else {
      resetFavorites();
    }
  }, [isAuthenticated, loadFavoriteIds, resetFavorites]);

  return (
    <div className="min-h-screen bg-transparent flex flex-col overflow-x-clip">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route
            path="/"
            element={PROMOTION_CAMPAIGN_ENABLED ? <Navigate to={getCampaignCatalogHref()} replace /> : <HomePage />}
          />
          <Route path="/sobre" element={<AboutPage />} />
          <Route path="/catalogo" element={<CatalogPage />} />
          <Route path="/produtos/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />
          <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
          <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/favoritos" element={<FavoritesPage />} />
          <Route path="/configuracoes" element={<AdminGuard><SettingsPage /></AdminGuard>} />
          <Route
            path="*"
            element={
              <div className="max-w-2xl mx-auto px-4 py-8">
                <EmptyState
                  title="Página não encontrada"
                  description="A página que você tentou acessar não existe ou foi removida."
                  action={<Link to="/"><Button>Voltar para a loja</Button></Link>}
                />
              </div>
            }
          />
        </Routes>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <MetaPixelPageView />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
